import type { IsoDate, Period } from '../dates';
import type { HolidayRule } from '../holidays';
import { isRecipeAllowed } from '../diet';
import type { Household } from '../household';
import type { Pantry } from '../pantry';
import { planRequirements, type Plan, type PlannedDay } from '../plan';
import type { PriceBook } from '../price-book';
import type { Recipe } from '../recipe';
import {
  buildShopping,
  type ShoppingCarryOver,
  type ShoppingContext,
  type ShoppingResult,
} from '../shopping';
import type { NutritionRow } from '../types';
import { consumptionCents } from './costs';

/**
 * DE: Übergang beim Weiterplanen ab heute: Bestand, feste vergangene Tage und noch zu kaufende
 * Rücklagen (Notfallvorrat).
 * EN: Hand-over when planning on from today: stock, fixed past days and reserves still to buy
 * (backup kit).
 */
export interface CarryOver extends ShoppingCarryOver {
  pastDays: readonly PlannedDay[];
  reservedAhead: readonly { date: IsoDate; recipeId: string }[];
}

export interface PlanRequest {
  household: Household;
  period: Period;
  recipes: readonly Recipe[];
  book: PriceBook;
  nutrition: Readonly<Record<string, NutritionRow>>;
  pantry: Pantry;
  locked: readonly { date: IsoDate; recipeId: string }[];
  seed: number;
  staples: readonly string[];
  holidays: readonly HolidayRule[];
  /**
   * DE: Frühester erlaubter Einkaufstag, z. B. heute beim Planen im Voraus.
   * EN: Earliest allowed shopping day, e.g. today when planning ahead.
   */
  earliestTrip?: IsoDate | undefined;
  carryOver?: CarryOver | undefined;
}

/**
 * DE: Einkaufskontext aus der Planungsanfrage.
 * EN: Shopping context derived from the planning request.
 */
export function shoppingContext(request: PlanRequest): ShoppingContext {
  const { period, household, book, pantry, staples, holidays, earliestTrip, carryOver } = request;
  return { period, household, book, pantry, staples, holidays, earliestTrip, carryOver };
}

/**
 * DE: Rezept nachschlagen; unbekannte IDs sind ein Fehler.
 * EN: Look up a recipe; unknown ids are an error.
 */
export function recipeOf(request: PlanRequest, id: string): Recipe {
  const recipe = request.recipes.find((candidate) => candidate.id === id);
  if (!recipe) throw new Error(`Unknown recipe: ${id}`);
  return recipe;
}

/**
 * DE: Die günstigsten erlaubten Notfallgerichte, die zu Beginn auf Vorrat gekauft werden.
 * EN: The cheapest allowed backup dinners, bought into stock at the start.
 */
export function backupKit(request: PlanRequest): Recipe[] {
  const context = shoppingContext(request);
  const cost = (recipe: Recipe) =>
    consumptionCents(recipe, recipe.baseServings, context, request.period.start);
  return request.recipes
    .filter(
      (r) => r.backupKit && isRecipeAllowed(r, request.household.members, request.book.catalog),
    )
    .sort((a, b) => cost(a) - cost(b) || a.id.localeCompare(b.id))
    .slice(0, request.household.backupKitDinners);
}

/**
 * DE: Einkäufe für einen Plan inklusive Notfallvorrat.
 * EN: Shopping for a plan including the backup kit.
 */
export function shopForPlan(plan: Plan, request: PlanRequest): ShoppingResult {
  const extra =
    request.carryOver?.reservedAhead ??
    backupKit(request).map((recipe) => ({ date: request.period.start, recipeId: recipe.id }));
  const requirements = planRequirements(plan, request.recipes, { staples: request.staples, extra });
  return buildShopping(requirements, shoppingContext(request));
}
